import { BehaviorSubject, catchError, EMPTY, filter, of, switchMap } from 'rxjs'
import { AuthApi } from 'src/api'
import { CommunityApi } from 'src/api/community'
import { ICommunityModel, IUserModel, IUserStatsModel } from 'src/interfaces'
import { CampaignMutationService, EventService, NotificationService } from 'src/services'
import { CreateJobTourService } from 'src/services/tour/create-job-tour.service'
import { GuestViewJobTourService } from 'src/services/tour/guest-view-job-tour.service'
import { PopupTourService } from 'src/services/tour/popup.service'
import { ShareJobTourService } from 'src/services/tour/share-job-tour.service'
import { AuthUtils, getAvatar, getFirstCompany, getFullName } from 'src/utils'
import { WithOutNextComplete } from 'types/rxjs'

/**
 * @deprecated
 */
const undefinedToEmptyString = (value?: string | null) => value ?? ''

const _isAuthenticated$ = new BehaviorSubject<boolean | null>(null)
const _profile$ = new BehaviorSubject<IUserModel>({} as IUserModel)
const _isRecruiter$ = new BehaviorSubject(false)
const _verifiedCompany$ = new BehaviorSubject(false)
/**
 * @deprecated
 */
const _formattedProfile$ = new BehaviorSubject<{
  lookupId?: string
  email?: string
  firstName?: string
  lastName?: string
  fullName?: string
  companyName?: string
  companyUrls?: string[]
  bio?: string
  avatar?: string
  companyLogo?: string
  video?: string
}>({})
const _communities$ = new BehaviorSubject<ICommunityModel[]>([])

export class AuthModule {
  static get isAuthenticated$(): WithOutNextComplete<typeof _isAuthenticated$> {
    return _isAuthenticated$
  }

  static get profile$(): WithOutNextComplete<typeof _profile$> {
    return _profile$
  }

  static get isRecruiter$(): WithOutNextComplete<typeof _isRecruiter$> {
    return _isRecruiter$
  }

  static get verifiedCompany$(): WithOutNextComplete<typeof _verifiedCompany$> {
    return _verifiedCompany$
  }

  /**
   * @deprecated
   */
  static get formattedProfile$(): WithOutNextComplete<typeof _formattedProfile$> {
    return _formattedProfile$
  }

  static get communities$(): WithOutNextComplete<typeof _communities$> {
    return _communities$
  }

  static updateStats(val: IUserStatsModel) {
    _profile$.next({
      ..._profile$.value,
      stats: {
        ..._profile$.value.stats,
        ...val
      }
    })
  }

  static guide(value: Partial<IUserModel['guide']>) {
    _profile$.next({
      ..._profile$.value,
      guide: { ..._profile$.value.guide, ...value }
    })
  }

  static updateProfile(value: Partial<IUserModel>) {
    _profile$.next({ ..._profile$.value, ...value })
  }

  static joinCommunity(...params: Parameters<typeof CommunityApi.joinCommunity>) {
    return CommunityApi.joinCommunity(...params).then((response) => {
      const found = _communities$.value.some((community) => community.code === response.data.code)
      if (!found) {
        _communities$.next([..._communities$.value, response.data])
      }
      return response
    })
  }

  static leaveCommunity(communityCode: string) {
    return CommunityApi.leaveCommunity(communityCode).then((response) => {
      _communities$.next(
        _communities$.value.filter((community) => community.code !== communityCode)
      )
      return response
    })
  }

  static authenticated(user: IUserModel) {
    _profile$.next(user)
  }

  static unauthorized() {
    _isAuthenticated$.value !== false && _isAuthenticated$.next(false)
    _profile$.next({} as IUserModel)
  }

  static signOut() {
    return AuthApi.logout().finally(() => {
      AuthModule.unauthorized()
      AuthUtils.clear()
      PopupTourService.hide()
      CreateJobTourService.stopTour()
      ShareJobTourService.stopTour()
      GuestViewJobTourService.stopTour()
      CampaignMutationService.reset()
      sessionStorage.clear()
    })
  }
}

_profile$
  .pipe(filter((profile) => !profile.id))
  .subscribe(() => {
    EventService.unbind()
    NotificationService.unbind()
  })

_profile$
  .pipe(filter((profile) => !!profile.id))
  .subscribe((profile) => {
    !_isAuthenticated$.value && _isAuthenticated$.next(true)
    if (profile?.email) {
      EventService.bind(profile?.email)
    }
    if (profile?.id) {
      NotificationService.bind(profile?.id)
    }
  })

_profile$.subscribe((profile) => {
  const isRecruiter = !!getFirstCompany(profile)?.CompanyUser?.isRecruiter
  isRecruiter !== _isRecruiter$.value && _isRecruiter$.next(isRecruiter)

  const isVerified = getFirstCompany(profile)?.CompanyUser?.isVerified || false
  isVerified !== _verifiedCompany$.value && _verifiedCompany$.next(isVerified)

  _formattedProfile$.next({
    lookupId: undefinedToEmptyString(profile.lookupId),
    email: undefinedToEmptyString(profile.email),
    firstName: undefinedToEmptyString(profile.firstName),
    lastName: undefinedToEmptyString(profile.lastName),
    fullName: getFullName(profile),
    companyName: undefinedToEmptyString(profile.companies?.[0]?.name),
    companyUrls: profile.companies?.[0]?.urls || [],
    bio: undefinedToEmptyString(profile.bio),
    avatar: undefinedToEmptyString(getAvatar(profile)),
    companyLogo: undefinedToEmptyString(profile.companies?.[0]?.logo?.url),
    video: undefinedToEmptyString(profile.pfv?.urlVideoSource)
  })
})

_profile$
  .pipe(
    filter(Boolean),
    switchMap(() => AuthModule.isAuthenticated$.value ? CommunityApi.joinedCommunities() : of({ data: { rows: [] } })),
    catchError(() => EMPTY)
  )
  .subscribe(({ data }) => {
    _communities$.next(data?.rows || [])
  })
